<template>
  <div class="banners">
    <!-- 标题区 -->
    <div class="page-header">
      <div>
        <h3>轮播图管理</h3>
        <p class="subtitle">管理小程序首页及活动页面的轮播Banner</p>
      </div>
      <div class="header-actions">
        <span class="count-info">共 {{ bannerList.length }} 张轮播图，最大支持 10 张</span>
        <el-button type="primary" @click="openBannerDialog('add')">
          <i class="el-icon-plus"></i> 新增轮播图
        </el-button>
      </div>
    </div>

    <!-- 轮播预览 -->
    <el-card shadow="never" class="preview-card">
      <div class="preview-title">轮播效果预览</div>
      <div class="preview-box" v-if="bannerList.length">
        <el-carousel :interval="3000" arrow="always" height="312px">
          <el-carousel-item v-for="item in enabledBanners" :key="item.id">
            <el-image :src="item.banner_img" style="width:100%;height:100%" fit="cover">
              <div slot="error" class="image-error">
                <i class="el-icon-picture-outline"></i>
                <span>暂无图片</span>
              </div>
            </el-image>
          </el-carousel-item>
        </el-carousel>
      </div>
      <el-empty v-else description="暂无轮播图" :image-size="100" />
    </el-card>

    <!-- 轮播图列表 -->
    <el-row :gutter="20" style="margin-top:20px">
      <el-col :span="12" v-for="item in bannerList" :key="item.id" style="margin-bottom:20px">
        <el-card shadow="hover" class="banner-card">
          <el-image :src="item.banner_img" style="width:100%;height:180px" fit="cover">
            <div slot="error" class="image-error">
              <i class="el-icon-picture-outline"></i>
              <span>暂无图片</span>
            </div>
          </el-image>
          <div class="banner-card-body">
            <div class="banner-info">
              <p class="banner-title">{{ item.banner_name || '未命名' }}</p>
              <p class="banner-meta">排序: {{ item.sort || 0 }} | 跳转类型: {{ jumpTypeText(item.jump_type) }}</p>
            </div>
            <div class="banner-actions">
              <el-switch v-model="item.status" :active-value="1" :inactive-value="0" active-color="#3a8ee6"
                @change="toggleStatus(item)" />
              <el-button type="text" size="mini" @click="openBannerDialog('edit', item)">编辑</el-button>
              <el-button type="text" size="mini" style="color:#f56c6c" @click="confirmDelete(item)">删除</el-button>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px" :close-on-click-modal="false">
      <el-form ref="bannerForm" :model="bannerForm" :rules="bannerRules" label-width="100px">
        <el-form-item label="轮播图名称" prop="title">
          <el-input v-model="bannerForm.title" placeholder="请输入轮播图名称（后台识别用）" />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="跳转类型">
              <el-select v-model="bannerForm.jump_type" style="width:100%">
                <el-option label="无跳转" :value="0" />
                <el-option label="商品页面" :value="1" />
                <el-option label="分类页面" :value="2" />
                <el-option label="外部链接" :value="3" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="排序值">
              <el-input-number v-model="bannerForm.sort" :min="0" placeholder="数字越大越靠前" style="width:100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="外部链接" v-if="bannerForm.jump_type === 3">
          <el-input v-model="bannerForm.target_url" placeholder="请输入外部链接URL" />
        </el-form-item>
        <el-form-item label="关联ID" v-if="bannerForm.jump_type === 1 || bannerForm.jump_type === 2">
          <el-input-number v-model="bannerForm.target_id" :min="0"
            :placeholder="bannerForm.jump_type === 1 ? '商品ID' : '分类ID'" style="width:100%" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="bannerForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="轮播图片" required>
          <el-upload class="banner-upload" :action="uploadUrl" :headers="uploadHeaders" :show-file-list="false"
            :on-success="handleBannerImgSuccess" :before-upload="beforeUpload" accept="image/*">
            <img v-if="bannerImgUrl" :src="bannerImgUrl" class="banner-img-preview" />
            <i v-else class="el-icon-plus banner-uploader-icon"></i>
          </el-upload>
          <p class="upload-tip">建议尺寸 750×312px，不超过5MB，支持 JPG/PNG/GIF</p>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveBanner">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getBanners, createBanner, updateBanner, destroyBanner } from '@/api'

export default {
  name: 'Banners',
  data() {
    return {
      bannerList: [],
      dialogVisible: false,
      dialogTitle: '新增轮播图',
      editId: null,
      bannerForm: { title: '', jump_type: 1, sort: 0, target_url: '', target_id: null, status: 1 },
      bannerImgUrl: '',
      bannerRules: {
        title: [{ required: true, message: '请输入轮播图名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    enabledBanners() {
      return this.bannerList.filter(b => b.status === 1)
    },
    uploadUrl() {
      return 'https://apighyx.zgyxszds.com/api/v1/supplier/upload'
    },
    uploadHeaders() {
      const token = localStorage.getItem('supplier_token')
      return token ? { Authorization: `Bearer ${token}` } : {}
    }
  },
  mounted() {
    this.loadBanners()
  },
  methods: {
    async loadBanners() {
      try {
        const res = await getBanners()
        this.bannerList = res.data || res || []
      } catch (e) { }
    },
    jumpTypeText(type) {
      const map = { 0: '无跳转', 1: '商品', 2: '分类', 3: '外链' }
      return map[type] || '未知'
    },
    openBannerDialog(mode, row) {
      this.dialogVisible = true
      this.bannerImgUrl = ''
      if (mode === 'add') {
        this.dialogTitle = '新增轮播图'
        this.editId = null
        this.bannerForm = { title: '', jump_type: 1, sort: 0, target_url: '', target_id: null, status: 1 }
      } else if (row) {
        this.dialogTitle = '编辑轮播图'
        this.editId = row.id
        this.bannerForm = {
          title: row.banner_name || row.title || '',
          jump_type: row.jump_type || 1,
          sort: row.sort || 0,
          target_url: row.target_url || '',
          target_id: row.target_id || null,
          status: row.status !== undefined ? row.status : 1
        }
        if (row.banner_img) {
          this.bannerImgUrl = row.banner_img
        }
      }
    },
    handleBannerImgSuccess(response) {
      if (response.result === 'ok' && response.message) {
        this.bannerImgUrl = response.message
      }
    },
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        this.$message.error('只能上传图片文件')
        return false
      }
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB')
        return false
      }
      return true
    },
    async saveBanner() {
      this.$refs.bannerForm.validate(async valid => {
        if (!valid) return
        if (!this.bannerImgUrl) {
          this.$message.error('请上传轮播图片')
          return
        }
        try {
          const data = {
            ...this.bannerForm,
            banner_img: this.bannerImgUrl
          }
          if (this.editId) {
            await updateBanner(this.editId, data)
            this.$message.success('更新成功')
          } else {
            await createBanner(data)
            this.$message.success('创建成功')
          }
          this.dialogVisible = false
          this.loadBanners()
        } catch (e) {
          this.$message.error(e.message || '操作失败')
        }
      })
    },
    async toggleStatus(item) {
      try {
        await updateBanner(item.id, { status: item.status })
        this.$message.success('状态已更新')
      } catch (e) {
        item.status = item.status === 1 ? 0 : 1
        this.$message.error('更新失败')
      }
    },
    confirmDelete(item) {
      this.$confirm('确定要删除该轮播图吗？删除后不可恢复。', '确认删除', { type: 'warning' }).then(async () => {
        try {
          await destroyBanner(item.id)
          this.$message.success('已删除')
          this.loadBanners()
        } catch (e) {
          this.$message.error(e.message || '删除失败')
        }
      }).catch(() => { })
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.page-header h3 {
  font-size: 20px;
  font-weight: bold;
  color: #303133;
  margin: 0;
}

.subtitle {
  font-size: 13px;
  color: #909399;
  margin: 4px 0 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.count-info {
  font-size: 13px;
  color: #909399;
}

.preview-card {
  margin-bottom: 0;
}

.preview-title {
  text-align: center;
  margin-bottom: 16px;
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.preview-box {
  background: #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
}

.banner-card {
  border-radius: 8px;
  overflow: hidden;
}

.banner-card-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
}

.banner-title {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin: 0;
}

.banner-meta {
  font-size: 12px;
  color: #c0c4cc;
  margin: 4px 0 0;
}

.banner-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.upload-tip {
  font-size: 12px;
  color: #c0c4cc;
  margin-top: 8px;
}

/* 图片上传 */
.banner-upload {
  display: inline-block;
}

.banner-img-preview {
  width: 320px;
  height: 134px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  cursor: pointer;
}

.banner-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 320px;
  height: 134px;
  line-height: 134px;
  text-align: center;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  display: inline-block;
}

.banner-uploader-icon:hover {
  border-color: #3a8ee6;
}

/* 图片加载失败占位 */
.image-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: #f5f7fa;
  color: #c0c4cc;
  font-size: 14px;
}

.image-error i {
  font-size: 40px;
  margin-bottom: 8px;
}
</style>
